/**
 * canvas-engine/presets/decompositionAdapters/resolvePreset.js
 *
 * Tiny shared helper used by the family/dynamic decomposition adapters so each
 * adapter can accept EITHER:
 *   - a transcribed preset DATA descriptor (used directly), OR
 *   - a section-type string, looked up in the family's transcribed presetData
 *     map (Task 3.1 values).
 *
 * This is what lets the section migration (Task 4.1) and the Canvas_Preset
 * registry (Task 3.4 / 4.4) dispatch purely by section type while authors /
 * tests can still hand a concrete descriptor.
 *
 * Lookup is by preset map KEY first (e.g. `educationHero`, `products`) and then
 * by each descriptor's own `sectionType` (e.g. the `products` preset whose
 * `sectionType` is `dynamicProducts`), so either spelling resolves.
 *
 * ZERO-REUSE CONSTRAINT: imports NOTHING (pure data helper). Deterministic, no
 * React, no DOM, no side effects.
 *
 * Requirements: 4.1, 5.1, 5.6, 5.7
 */

/** True for a non-null, non-array plain object. */
function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Trimmed string, or '' when not a usable string. */
function pickString(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * The union of section types a preset map handles: every map KEY plus every
 * descriptor `sectionType`. Stable, de-duplicated, insertion-ordered.
 *
 * @param {object} presetMap a transcribed presetData map
 * @returns {string[]}
 */
export function handledTypesOf(presetMap) {
  const map = isPlainObject(presetMap) ? presetMap : {};
  const out = [];
  const seen = new Set();
  const add = (t) => {
    const s = pickString(t);
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  };
  const keys = Object.keys(map);
  for (let i = 0; i < keys.length; i += 1) add(keys[i]);
  for (let i = 0; i < keys.length; i += 1) {
    const entry = map[keys[i]];
    if (isPlainObject(entry)) add(entry.sectionType);
  }
  return out;
}

/**
 * Resolve a `sectionType_or_data` argument against a family preset map into a
 * concrete descriptor + the resolved section type.
 *
 * @param {(string|object)} input a section-type string OR a DATA descriptor
 * @param {object} presetMap the family's transcribed presetData map
 * @param {object} [options] adapter options; `options.sectionType` overrides
 *   the descriptor's own `sectionType` for routing + provenance
 * @returns {{ data: object, sectionType: string }} resolved descriptor + type
 *   (`data` is `{}` when a string does not match any preset)
 */
export function resolvePreset(input, presetMap, options = {}) {
  const opts = isPlainObject(options) ? options : {};
  const map = isPlainObject(presetMap) ? presetMap : {};

  // A descriptor passed directly is used as-is.
  if (isPlainObject(input)) {
    const sectionType = pickString(opts.sectionType) || pickString(input.sectionType) || 'section';
    return { data: input, sectionType };
  }

  // A string section type is looked up by key, then by descriptor sectionType.
  const key = pickString(input);
  let data = null;
  if (key && Object.prototype.hasOwnProperty.call(map, key) && isPlainObject(map[key])) {
    data = map[key];
  } else if (key) {
    const mapKeys = Object.keys(map);
    for (let i = 0; i < mapKeys.length; i += 1) {
      const entry = map[mapKeys[i]];
      if (isPlainObject(entry) && pickString(entry.sectionType) === key) {
        data = entry;
        break;
      }
    }
  }

  const sectionType = pickString(opts.sectionType) || (data && pickString(data.sectionType)) || key || 'section';
  return { data: data || {}, sectionType };
}

/**
 * Build the `buildSectionCanvas` options for a resolved descriptor: family
 * adapters lay everything out at the fixed Reference_Width and derive a stable
 * id prefix from the section type, while still honoring any caller overrides.
 *
 * @param {string} sectionType resolved section type
 * @param {number} frameWidth the reference frame width
 * @param {object} [options] caller options (override the derived defaults)
 * @returns {object} normalized `buildSectionCanvas` options
 */
export function adapterOptions(sectionType, frameWidth, options = {}) {
  const opts = isPlainObject(options) ? options : {};
  return {
    frameWidth,
    ...opts,
    sectionType,
    idPrefix: pickString(opts.idPrefix) || sectionType,
  };
}

export default resolvePreset;
